import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';

export interface PageHeaderProps {
  title: string;
  /** One plain sentence: what this screen is for. */
  about?: ReactNode | undefined;
  actions?: ReactNode | undefined;
  /** A link above the title, such as "← All rate cards". */
  back?: ReactNode | undefined;
}

export function PageHeader({ title, about, actions, back }: PageHeaderProps) {
  return (
    <header className="eb-pagehead">
      {back ? <div className="eb-pagehead__back">{back}</div> : null}
      <div className="eb-pagehead__row">
        <div>
          <h1 className="eb-pagehead__title">{title}</h1>
          {about ? <p className="eb-pagehead__about">{about}</p> : null}
        </div>
        {actions ? <div className="eb-pagehead__actions">{actions}</div> : null}
      </div>
    </header>
  );
}

export interface StatProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode | undefined;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | undefined;
}

/** One number with a label, for summaries. */
export function Stat({ label, value, hint, tone = 'neutral' }: StatProps) {
  return (
    <div className="eb-stat" data-tone={tone}>
      <div className="eb-stat__label">{label}</div>
      <div className="eb-stat__value">{value}</div>
      {hint ? <div className="eb-stat__hint">{hint}</div> : null}
    </div>
  );
}

export interface FormGridProps extends HTMLAttributes<HTMLDivElement> {
  /** Columns on a wide screen. Always one column on a phone. */
  cols?: 1 | 2 | 3 | 4 | undefined;
}

/** Lays form fields out in a tidy grid. Give a field `className="eb-span-all"` to make it full width. */
export function FormGrid({ cols = 2, className, children, ...rest }: FormGridProps) {
  return (
    <div className={cx('eb-formgrid', className)} data-cols={cols} {...rest}>
      {children}
    </div>
  );
}

export interface FormSectionProps {
  title: string;
  hint?: ReactNode | undefined;
  actions?: ReactNode | undefined;
  children: ReactNode;
}

/** A titled group of fields inside a form. */
export function FormSection({ title, hint, actions, children }: FormSectionProps) {
  return (
    <section className="eb-formsection">
      <div className="eb-formsection__head">
        <h3 className="eb-formsection__title">{title}</h3>
        {actions}
      </div>
      {hint ? <p className="eb-formsection__hint">{hint}</p> : null}
      {children}
    </section>
  );
}

export interface CalloutProps {
  tone?: 'info' | 'success' | 'warning' | 'danger' | undefined;
  title?: string | undefined;
  children: ReactNode;
  role?: 'status' | 'alert' | undefined;
}

/** A boxed message: what happened, or what to do next. */
export function Callout({ tone = 'info', title, children, role }: CalloutProps) {
  return (
    <div
      className="eb-callout"
      data-tone={tone}
      role={role ?? (tone === 'danger' ? 'alert' : undefined)}
    >
      {title ? <strong className="eb-callout__title">{title}</strong> : null}
      <div>{children}</div>
    </div>
  );
}

export interface ToolbarProps {
  children: ReactNode;
  className?: string | undefined;
  /** The walkthrough target (18 §18.1): the whole row of filters, not one box in it. */
  'data-tour'?: string | undefined;
}

/** A row of filters and buttons above a table. */
export function Toolbar({ children, className, 'data-tour': tour }: ToolbarProps) {
  return (
    <div className={cx('eb-toolbar', className)} data-tour={tour}>
      {children}
    </div>
  );
}
